/**
 * Sign-out hook
 *
 * Provides sign-out functionality
 */

import { useAuthActions } from "@convex-dev/auth/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import i18n from "i18next";
import type { AuthError } from "../types";

export interface UseSignOutOptions {
  /** Called on successful sign-out */
  onSuccess?: () => void;
  /** Called on sign-out error */
  onError?: (error: AuthError) => void;
}

export interface UseSignOutReturn {
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Whether sign-out is in progress */
  isLoading: boolean;
  /** Current error message if any */
  error: string | null;
}

/**
 * Hook for sign-out functionality
 *
 * Handles:
 * - Sign-out from Convex auth
 * - Clearing React Query cache
 * - Error handling
 *
 * @param options - Optional callbacks for success/error
 * @returns Sign-out function and state
 *
 * @example
 * ```tsx
 * function LogoutButton() {
 *   const { signOut, isLoading } = useSignOut({
 *     onSuccess: () => router.push("/login"),
 *   });
 *
 *   return (
 *     <button onClick={signOut} disabled={isLoading}>
 *       {isLoading ? "Signing out..." : "Sign Out"}
 *     </button>
 *   );
 * }
 * ```
 */
export function useSignOut(options: UseSignOutOptions = {}): UseSignOutReturn {
  const { signOut: convexSignOut } = useAuthActions();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signOut = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Sign out from Convex auth
      await convexSignOut();

      // Clear React Query cache to remove stale user data
      queryClient.clear();

      options.onSuccess?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : i18n.t("auth:errors.signOutFailed");
      const authError: AuthError = {
        code: "INTERNAL_ERROR",
        message: errorMessage,
      };

      setError(errorMessage);
      options.onError?.(authError);
    } finally {
      setIsLoading(false);
    }
  }, [convexSignOut, queryClient, options]);

  return {
    signOut,
    isLoading,
    error,
  };
}
